import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export interface ContentTone {
  id: string;
  name: string;
  emoji: string;
  description: string;
}

interface ContentToneSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export const ContentToneSelect: React.FC<ContentToneSelectProps> = ({
  value,
  onChange,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t, i18n } = useTranslation();
  // Force rerender when language changes
  const [, forceUpdate] = useState({});
  
  // Force rerender when language changes
  useEffect(() => {
    forceUpdate({}); // Force a component rerender
  }, [i18n.language]);
  
  // Define content tones with translations using useMemo to refresh when language changes
  const contentTones = React.useMemo(() => {
    return [
      {
        id: 'inspirational',
        name: t('contentCreation.form.tones.inspirational', 'Inspirational'),
        emoji: '✨',
        description: t('contentCreation.form.toneDescriptions.inspirational', 'Uplifting and motivational content that inspires action and positive change')
      },
      {
        id: 'professional',
        name: t('contentCreation.form.tones.professional', 'Professional'),
        emoji: '👔',
        description: t('contentCreation.form.toneDescriptions.professional', 'Formal and business-oriented content that establishes expertise and authority')
      },
      {
        id: 'casual',
        name: t('contentCreation.form.tones.casual', 'Casual'),
        emoji: '😊',
        description: t('contentCreation.form.toneDescriptions.casual', 'Relaxed and friendly content that creates a comfortable, approachable atmosphere')
      },
      {
        id: 'educational',
        name: t('contentCreation.form.tones.educational', 'Educational'),
        emoji: '📚',
        description: t('contentCreation.form.toneDescriptions.educational', 'Informative content focused on teaching and sharing knowledge')
      },
      {
        id: 'friendly',
        name: t('contentCreation.form.tones.friendly', 'Friendly'),
        emoji: '🤝',
        description: t('contentCreation.form.toneDescriptions.friendly', 'Warm and welcoming content that builds personal connections')
      },
      {
        id: 'funny',
        name: t('contentCreation.form.tones.funny', 'Funny'),
        emoji: '😂',
        description: t('contentCreation.form.toneDescriptions.funny', 'Humorous and entertaining content that makes people laugh and engages through comedy')
      }
    ];
  }, [t, i18n.language]); // Re-create the array when t or language changes
  
  const selectedTone = contentTones.find(tone => tone.id === value) || contentTones[0];

  return (
    <div className="relative">
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation(); // Prevent event from bubbling up
          setIsOpen(!isOpen);
        }}
        className={`w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white text-left ${className}`}
      >
        <span className="flex items-center gap-2">
          <span className="text-lg">{selectedTone.emoji}</span>
          <span className="text-gray-700">{selectedTone.name}</span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-10"
            onClick={(e) => {
              e.stopPropagation(); // Prevent event from bubbling up
              setIsOpen(false);
            }}
          />
          <div className="absolute left-0 right-0 mt-2 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
            <div className="py-1">
              {contentTones.map(tone => (
                <button
                  type="button"
                  key={tone.id}
                  onClick={(e) => {
                    e.stopPropagation(); // Prevent event from bubbling up
                    onChange(tone.id);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 ${
                    tone.id === value
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:text-primary-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{tone.emoji}</span>
                    <div>
                      <div className="font-medium">{tone.name}</div>
                      <div className="text-xs text-gray-500">{tone.description}</div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};